import setCookie from './utils/setCookie';

/**
 * A custom element for enhancing the search bar section into one that is collapsed by default in narrow viewports.
 *
 * @module iabbb-search-bar
 */
class SearchBar extends HTMLElement {
  constructor() {
    super();

    this.render = () => {
      this.isResizeListenerInitialized = false;

      this.querySelector('form').addEventListener('submit', this.handleFormSubmit);

      // Does browser support the HTML dialog element?
      // If not, we'll fall back to rendering the search bars directly on the page.
      if (typeof HTMLDialogElement === 'function') {
        this.querySelector('.search-bar').setAttribute('data-enhanced', 'true');
        this.createMobileSearchBar();
        this.createMobileSearchDialog();
        this.createMobileSearchButton();
      }
    };
  }

  connectedCallback() {
    this.render();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  /**
   * Emit a custom event
   * @param {string} type - The event type
   * @param {object} detail - Any details to pass along with the event
   */
  emit = (type, detail = {}) => {
    // Create a new event
    const event = new CustomEvent(`${this.localName}:${type}`, {
      bubbles: true,
      cancelable: true,
      detail: detail,
    });

    // Dispatch the event
    return this.dispatchEvent(event);
  };

  handleFormSubmit = (e) => {
    if ('FormData' in window) {
      const formValues = Object.fromEntries(new FormData(e.target));

      const query = formValues.find_text;
      const location = formValues.find_loc;

      this.emit('execute', { query, location });

      /*
       ** If user searches for an accredited category,
       ** set their preference to search for only accredited businesses.
       */
      if (query.startsWith('accredited ')) {
        setCookie('iabbb_accredited_search', 'true');
        setCookie('iabbb_accredited_toggle_state', 'seen');
      }
    }
  };

  createMobileSearchBar = () => {
    const mobileSearchBar = document.createElement('div');
    mobileSearchBar.className = 'mobile-search-bar';
    this.appendChild(mobileSearchBar);
  };

  createMobileSearchButton = () => {
    const wrapper = document.createElement('div');
    wrapper.className = 'wrapper';

    const button = document.createElement('button');
    button.className = 'dialog-trigger';
    button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" aria-hidden="true" focusable="false" height="1em" width="1em" fill="currentColor"><path d="M416 208c0 45.9-14.9 88.3-40 122.7l126.6 126.7c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0s208 93.1 208 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"></path></svg>Find local businesses`;
    button.onclick = () => {
      this.querySelector('.mobile-search-bar dialog').showModal();
      this.querySelector('.mobile-search-bar iabbb-find-autocomplete input').focus();

      if (!this.isResizeListenerInitialized) {
        this.initializeResizeListener();
      }
    };

    wrapper.appendChild(button);
    this.querySelector('.mobile-search-bar').prepend(wrapper);
  };

  // Close dialog if it is open, and user resizes to a desktop-sized window.
  // If this doesn't happen, keyboard users will run into a fatal error when they cannot move focus back to the rest of the document.
  initializeResizeListener = () => {
    window.addEventListener('resize', this.onResize, { passive: true });
    this.isResizeListenerInitialized = true;
  };

  createMobileSearchDialog = () => {
    const mobileSearchDialog = document.createElement('dialog');
    mobileSearchDialog.tabIndex = -1;

    const mobileSearchForm = this.querySelector('form').cloneNode(false);
    mobileSearchForm.setAttribute('name', 'mobile-search');

    const mobileSearchFormActions = document.createElement('div');
    mobileSearchFormActions.className = 'actions';

    const dialogCloseButton = document.createElement('button');
    dialogCloseButton.type = 'button';
    dialogCloseButton.className = 'bds-button-unstyled search-dialog-close';
    dialogCloseButton.innerHTML =
      '<span class="visually-hidden">close</span><svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 384 512" aria-hidden="true" focusable="false" height="1em" width="1em"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3l105.4 105.3c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z"></path></svg>';
    dialogCloseButton.onclick = (e) => {
      e.preventDefault();
      mobileSearchDialog.close();
    };

    const searchButton = document.createElement('button');
    searchButton.className = 'bds-button';
    searchButton.setAttribute('data-type', 'search');
    searchButton.innerText = 'Search';

    mobileSearchFormActions.appendChild(dialogCloseButton);
    mobileSearchFormActions.appendChild(searchButton);

    mobileSearchForm.appendChild(mobileSearchFormActions);

    mobileSearchDialog.appendChild(mobileSearchForm);

    const findTypeahead = this.createFindAutocomplete();
    const nearTypeahead = this.createNearAutocomplete();

    mobileSearchForm.appendChild(findTypeahead);
    mobileSearchForm.appendChild(nearTypeahead);

    this.querySelector('.mobile-search-bar').appendChild(mobileSearchDialog);
  };

  createFindAutocomplete = () => {
    const findAutocomplete = document.createElement('iabbb-find-autocomplete');
    findAutocomplete.innerHTML = `<div class="input-group"><label for="find_text-mobile"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" aria-hidden="true" focusable="false" height="1em" width="1em"><path d="M416 208c0 45.9-14.9 88.3-40 122.7l126.6 126.7c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0s208 93.1 208 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"></path></svg><span class="visually-hidden">Find</span></label><input id="find_text-mobile" name="find_text" placeholder="businesses, category" autocomplete="off" autocapitalize="none"></div>`;
    findAutocomplete.showOptionsOnFocus = true;
    findAutocomplete.className = 'autocomplete';

    return findAutocomplete;
  };

  createNearAutocomplete = () => {
    const container = document.createElement('div');
    container.className = 'near-typeahead';

    const nearAutocomplete = document.createElement('iabbb-near-autocomplete');
    nearAutocomplete.className = 'autocomplete';
    nearAutocomplete.innerHTML = `<div class="input-group"><label for="find_loc-mobile"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" aria-hidden="true" focusable="false" height="1em" width="1em"><path d="M215.7 499.2C267 435 384 279.4 384 192 384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2 12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"></path></svg><span class="visually-hidden">Near</span></label><input id="find_loc-mobile" name="find_loc" placeholder="city, state or zip" autocomplete="off" autocapitalize="none"></div>`;

    container.appendChild(nearAutocomplete);

    const countryCombobox = document.createElement('iabbb-country-custom-select');
    countryCombobox.className = 'enhanced-select';

    countryCombobox.innerHTML = `<label class="visually-hidden" for="find_country-mobile">Country</label>
      <select id="find_country-mobile" name="find_country">
        <option value="USA">US</option>
        <option value="CAN">CA</option>
      </select>`;

    container.appendChild(countryCombobox);

    return container;
  };

  onResize = () => {
    this.closeDialogOnResize(document.body);
  };

  closeDialogOnResize = (element) => {
    // 👇 should equal the breakpoint where we switch to .mobile-search-bar
    if (element.clientWidth < 992) {
      return;
    }

    const dialog = this.querySelector('dialog');

    if (dialog.open) {
      dialog.close();
    }
  };
}

customElements.define('iabbb-search-bar', SearchBar);
